import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const privacyContent = `# Divscore Privacy Policy

**Last Updated:** April 3rd, 2025

Guernica LLC ("Company," "we," "us," or "our") operates Divscore (the "Service"), accessible via [Divscore.io](https://divscore.io). We are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, share, and protect your personal information. By using our Service, you acknowledge that you have read and understand this Policy.

---

## 1. Scope of this Policy

This Privacy Policy applies to information we collect through:
- Our website at [Divscore.io](https://divscore.io)  
- Any related services, tools, or applications offered by Divscore  
- Email and other electronic communications sent to or from us  

It does **not** apply to information collected through third-party websites or services that may link to or be accessible from our Service.

---

## 2. Information We Collect

We may collect several types of information from you, including:

1. **Personal Information**  
   - **Contact Details:** Such as your name, email address, mailing address, and phone number.  
   - **Account Credentials:** Username and password when you create an account with us.

2. **Financial Data**  
   - **Portfolio Details:** Information about your investments, stocks, and other financial holdings that you choose to provide.  
   - **Transaction Information:** Details about your subscription plans and payment history (processed via third-party payment processors; we do not store full credit card numbers on our servers).

3. **Usage Data**  
   - **Device Information:** IP address, browser type, operating system, and device identifiers (if using mobile apps).  
   - **Browsing Data:** Pages visited, links clicked, time and date of visits, and other diagnostic data related to how you interact with our Service.

4. **Cookies and Tracking Technologies**  
   - We use **cookies**, **web beacons**, and similar technologies to collect information about your activities over time and across different sites or platforms. For more details, see **Section 5: Cookies and Similar Technologies**.

5. **Communications**  
   - Any information you provide when you communicate with us via email, surveys, or customer support requests.

---

## 3. How We Use Your Information

We use the information we collect for various purposes, including:

1. **Providing and Improving Our Service**  
   - To create, maintain, and secure your account.  
   - To process your subscription fees and manage payment transactions.  
   - To generate portfolio scores, comparisons, and analytics based on your inputs.  
   - To analyze usage trends and improve our website and user experience.

2. **Communications**  
   - To respond to your inquiries and provide customer support.  
   - To send you administrative updates such as account confirmations, technical notices, and security alerts.  
   - To send you marketing or promotional materials (where permitted by law). You can opt out of marketing communications at any time.

3. **Compliance and Enforcement**  
   - To enforce our Terms of Service, protect our rights, privacy, safety, or property, and that of our users and other third parties.  
   - To comply with legal obligations, such as responding to lawful requests by public authorities.

4. **Research and Development**  
   - To aggregate or de-identify personal information so it can no longer identify you personally, then use it for analytics, benchmarking, and internal research.

---

## 4. Legal Basis for Processing Personal Data (EEA/UK Users)

If you are located in the European Economic Area or the United Kingdom, we process your personal information based on one or more of the following legal bases:

- **Consent:** Where you have given us explicit consent to process your information.  
- **Contractual Necessity:** Where processing is necessary for the performance of a contract with you (e.g., creating an account, providing subscription services).  
- **Legitimate Interests:** Where processing is necessary for our legitimate interests and does not override your fundamental rights and freedoms (e.g., improving the user experience, preventing fraud).

---

## 5. Cookies and Similar Technologies

1. **What Are Cookies?**  
   - Cookies are small text files that are placed on your device when you visit a website. They help us remember your preferences and enhance your user experience.

2. **Types of Cookies We Use**  
   - **Essential Cookies:** Necessary for the functionality of our Service (e.g., keeping you logged in).  
   - **Analytics Cookies:** Help us understand user behavior and improve our Service (e.g., Google Analytics).  
   - **Marketing Cookies:** Used to track browsing habits and serve relevant ads (if applicable).

3. **Managing Cookies**  
   - You can usually set your browser to refuse some or all cookies or to alert you when cookies are being sent. However, if you disable or refuse cookies, some parts of the Service may become inaccessible or not function properly.

---

## 6. How We Share Your Information

We do **not** sell or rent your personal information. We may share your information in the following ways:

1. **Service Providers**  
   - We may share personal information with third-party vendors, consultants, and service providers who perform services on our behalf (e.g., payment processors, hosting providers, analytics providers). These third parties are contractually bound to protect your information and only use it for specified purposes.

2. **Business Transfers**  
   - In the event of a merger, acquisition, reorganization, or sale of assets, your personal information may be transferred as part of the transaction. We will notify you of any change in ownership or use of your personal information.

3. **Legal Obligations**  
   - We may disclose your information when required by law or in response to valid requests by government or law enforcement agencies.

4. **Protection of Rights**  
   - We may share information if we believe it's necessary to detect, prevent, or address fraud, security, or technical issues, or to protect our rights, property, or user safety.

---

## 7. Data Retention

We retain your personal information only for as long as is necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Once data is no longer needed, we will securely destroy or anonymize it.

---

## 8. Data Security

We maintain reasonable administrative, technical, and physical safeguards to protect your personal information against accidental, unlawful, or unauthorized destruction, loss, alteration, access, disclosure, or use. However, no method of transmission over the internet or method of electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.

---

## 9. Children's Privacy

Our Service is **not** intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we might have any information from or about a child, please contact us immediately at contact@guernicaholdings.com so that we can take appropriate measures.

---

## 10. International Data Transfers

We are based in the United States, and your personal information may be transferred to, stored in, or processed in the U.S. If you access our Service from outside the United States, please be aware that your information may be subject to U.S. laws, which may differ from your country's data protection laws. In such cases, we will take steps to ensure that appropriate safeguards are in place to protect your data.

---

## 11. Your Rights and Choices

Depending on your jurisdiction, you may have certain rights regarding your personal information, such as:

- **Access**: The right to request a copy of the personal information we hold about you.  
- **Correction**: The right to request corrections to any inaccurate or incomplete information.  
- **Deletion**: The right to request deletion of your personal information, subject to certain legal exceptions.  
- **Opt-Out of Marketing**: The right to opt out of receiving marketing communications at any time by clicking the "unsubscribe" link in our emails or contacting us directly.  

If you wish to exercise any of these rights, please contact us at contact@guernicaholdings.com. We will respond to your request within a reasonable timeframe and in accordance with applicable laws.

---

## 12. Third-Party Websites and Services

Our Service may contain links to third-party websites or services that are not owned or controlled by us. We are not responsible for the privacy practices of such third parties. We encourage you to read the privacy policies of every website and service you visit.

---

## 13. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. When we do, we will post the updated policy on this page and revise the "Last Updated" date at the top. If we make significant changes, we will provide you with additional notice (e.g., by email or by placing a prominent notice on the Service).

Your continued use of our Service after any such changes indicates your acceptance of the updated Privacy Policy.

---

## 14. Contact Us

If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:

**Guernica LLC**  
1309 Cofeen Ave, Sheridan, WY 82801, USA
contact@guernicaholdings.com`;

export default function Privacy() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ 
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: 4,
        '& h1': { mb: 3 },
        '& h2': { mt: 4, mb: 2 },
        '& p': { mb: 2 },
        '& ul': { mb: 2 },
      }}>
        <ReactMarkdown>{privacyContent}</ReactMarkdown>
      </Box>
    </Container>
  );
} 